import React, { useCallback, useMemo, useRef, useState } from 'react';
import { format, parseISO, differenceInDays, addYears } from 'date-fns';
import type {
  TimesheetPeriod,
  TimesheetDateRange,
} from '../../types/timesheet.types';

// ─── Period option definitions ──────────────────────────────────────────────

export const TIMESHEET_PERIOD_OPTIONS: Array<{
  value: TimesheetPeriod;
  label: string;
}> = [
  { value: 'current_week', label: 'Current Week' },
  { value: 'previous_week', label: 'Previous Week' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'last_quarter', label: 'Previous Quarter' },
  { value: 'current_year', label: 'Current Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

// Max custom range: 1 year (365 days)
const MAX_CUSTOM_DAYS = 365;

export interface TimesheetPeriodPickerProps {
  period: TimesheetPeriod;
  dateRange: TimesheetDateRange;
  onPeriodChange: (period: TimesheetPeriod) => void;
  onCustomRangeChange: (start: string, end: string) => void;
}

const TimesheetPeriodPicker: React.FC<TimesheetPeriodPickerProps> = ({
  period,
  dateRange,
  onPeriodChange,
  onCustomRangeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [customError, setCustomError] = useState<string | null>(null);

  // Close on outside click
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  const selectedLabel = useMemo(() => {
    const opt = TIMESHEET_PERIOD_OPTIONS.find((o) => o.value === period);
    return opt?.label ?? 'Current Week';
  }, [period]);

  const handleSelect = useCallback(
    (value: TimesheetPeriod) => {
      onPeriodChange(value);
      if (value !== 'custom') {
        setIsOpen(false);
        setCustomError(null);
      }
    },
    [onPeriodChange],
  );

  const validateAndSetCustomRange = useCallback(
    (start: string, end: string) => {
      if (!start || !end) return;

      const startDate = parseISO(start);
      const endDate = parseISO(end);

      if (endDate < startDate) {
        setCustomError('End date must be after start date');
        return;
      }

      const days = differenceInDays(endDate, startDate);
      if (days > MAX_CUSTOM_DAYS) {
        setCustomError(`Custom range cannot exceed ${MAX_CUSTOM_DAYS} days (1 year)`);
        return;
      }

      setCustomError(null);
      onCustomRangeChange(start, end);
    },
    [onCustomRangeChange],
  );

  // Max selectable end date: start + 1 year
  const maxEndDate = useMemo(() => {
    if (dateRange.start) {
      return format(addYears(parseISO(dateRange.start), 1), 'yyyy-MM-dd');
    }
    return undefined;
  }, [dateRange.start]);

  return (
    <div
      className="rp-ts-period-picker"
      ref={containerRef}
      onBlur={handleBlur}
      tabIndex={-1}
    >
      <button
        className="rp-ts-period-picker__trigger"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="rp-ts-period-picker__label">{selectedLabel}</span>
        <svg
          className={`rp-ts-period-picker__arrow ${isOpen ? 'rp-ts-period-picker__arrow--open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="rp-ts-period-picker__dropdown">
          <ul className="rp-ts-period-picker__options">
            {TIMESHEET_PERIOD_OPTIONS.map((opt) => (
              <li key={opt.value}>
                <button
                  className={`rp-ts-period-picker__option ${period === opt.value ? 'rp-ts-period-picker__option--active' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                  type="button"
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>

          {period === 'custom' && (
            <div className="rp-ts-period-picker__custom">
              <div className="rp-ts-period-picker__custom-row">
                <label className="rp-ts-period-picker__custom-label">
                  From
                  <input
                    type="date"
                    className="rp-ts-period-picker__custom-input"
                    value={dateRange.start}
                    onChange={(e) =>
                      validateAndSetCustomRange(e.target.value, dateRange.end)
                    }
                  />
                </label>
                <label className="rp-ts-period-picker__custom-label">
                  To
                  <input
                    type="date"
                    className="rp-ts-period-picker__custom-input"
                    value={dateRange.end}
                    max={maxEndDate}
                    onChange={(e) =>
                      validateAndSetCustomRange(dateRange.start, e.target.value)
                    }
                  />
                </label>
              </div>
              {customError && (
                <div className="rp-ts-period-picker__custom-error">
                  {customError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimesheetPeriodPicker;
